import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, LargeTopStepper, Paper, Pill, Stack, Typography} from 'views/components';
import {AccountTreeOutlined as WorkflowIcon} from 'views/icons';
import {translate} from 'views/module';

import WorkflowCompleteDialog from './WorkflowCompleteDialog';
import WorkflowTimeline from './WorkflowTimeline';
import {LEAD_INTAKE_STEPS, TONE_PILL_PROPS, canCompleteManually, describeWorkflow, humanizeKind} from './workflow_status';
import {formatDateTime, personName, translateOr} from '../format';

const DetailCell = (props) => {
  const {label, value} = props;

  return (
    <Stack
      sx={{
        gap: '6px',
        minWidth: 0,
        backgroundColor: '#FFFFFF',
        // Each cell draws its share of the grid lines, so a short last row leaves white space
        // rather than the grey of a gap-coloured grid background.
        boxShadow: '0 0 0 0.5px #E8E4DE',
        px: 2,
        py: 1.5,
      }}
    >
      <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#A8A49E'}}>
        {label}
      </Typography>
      <Typography sx={{fontWeight: 700, fontSize: '12px', lineHeight: '16px', color: '#000000', wordBreak: 'break-word'}}>
        {value || '-'}
      </Typography>
    </Stack>
  );
};
DetailCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const WorkflowCard = (props) => {
  const {clientId, workflow, completionRequested, onCompleted} = props;
  const [showTimeline, setShowTimeline] = React.useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = React.useState(false);

  const description = React.useMemo(
      () => describeWorkflow(workflow, completionRequested),
      [workflow, completionRequested],
  );
  const canComplete = canCompleteManually(workflow, completionRequested);

  const handleCompleted = (workflowId) => {
    setShowCompleteDialog(false);
    onCompleted(workflowId);
  };

  const lead = workflow.leadIntake;
  const booking = lead?.booking;

  let finalResult;
  if (lead?.lostReason || workflow.outcome === 'CLOSED_LOST') {
    const reason = lead?.lostReason && translateOr(`loomLead.LoomLeadDetail.Workflows.LostReason.${lead.lostReason}`, humanizeKind(lead.lostReason));
    finalResult = [translate('loomLead.LoomLeadDetail.Workflows.Outcome.CLOSED_LOST'), reason].filter(Boolean).join(' — ');
  } else if (booking) {
    finalResult = `${translate('loomLead.LoomLeadDetail.Workflows.Details.Booking')} — ${formatDateTime(booking.scheduledStartTime)}`;
  }

  const stepLabels = LEAD_INTAKE_STEPS.map((step) => {
    const labelKey = step === 'Finished' && description.finishedStep ? description.finishedStep : step;
    return translate(`loomLead.LoomLeadDetail.Workflows.Steps.${labelKey}`);
  });

  const details = [
    {
      key: 'submitter',
      label: translate('loomLead.LoomLeadDetail.Workflows.Details.Submitter'),
      value: personName(lead?.submitter),
    },
    {
      key: 'contact',
      label: translate('loomLead.LoomLeadDetail.Workflows.Details.Contact'),
      value: [lead?.contactEmail, lead?.contactPhoneNumber].filter(Boolean).join(' / '),
    },
    {
      key: 'channel',
      label: translate('loomLead.LoomLeadDetail.Workflows.Details.Channel'),
      value: lead?.selectedChannel && translateOr(`loomLead.LoomLeadDetail.Workflows.Channel.${lead.selectedChannel}`, lead.selectedChannel),
    },
    {
      key: 'followUps',
      label: translate('loomLead.LoomLeadDetail.Workflows.Details.FollowUps'),
      value: lead ? String(lead.followUpRoundsSent) : null,
    },
    {
      key: 'outcome',
      label: translate('loomLead.LoomLeadDetail.Workflows.Details.Outcome'),
      value: workflow.outcome && translateOr(`loomLead.LoomLeadDetail.Workflows.Outcome.${workflow.outcome}`, humanizeKind(workflow.outcome)),
    },
    {
      key: 'finalResult',
      label: translate('loomLead.LoomLeadDetail.Workflows.Details.FinalResult'),
      value: finalResult,
    },
  ].filter(Boolean);

  // TEAM_REVIEW is expected, not a fault, so it gets a softer note than the attention reasons.
  const isInTeamReview = description.state === 'TEAM_REVIEW' && !description.attention.length;

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <WorkflowIcon sx={{mr: 1}} />
          <Typography variant='h6'>
            {translateOr(`loomLead.LoomLeadDetail.Workflows.Type.${workflow.workflowType}`, humanizeKind(workflow.workflowType))}
          </Typography>
        </Box>
        <Stack direction='row' alignItems='center' sx={{gap: 1}}>
          {
            canComplete &&
            <Button
              variant='outlined'
              size='small'
              onClick={() => setShowCompleteDialog(true)}
              sx={{textTransform: 'none'}}
            >
              {translate('loomLead.LoomLeadDetail.Workflows.Complete.Action')}
            </Button>
          }
          <Pill
            label={translate(`loomLead.LoomLeadDetail.Workflows.State.${description.state}`)}
            {...TONE_PILL_PROPS[description.tone]}
          />
        </Stack>
      </Box>
      <Stack spacing={2} sx={{p: 2}}>
        <Stack direction='row' sx={{gap: '7px', flexWrap: 'wrap'}}>
          <Pill
            label={translate('loomLead.LoomLeadDetail.Workflows.Started', {value: formatDateTime(workflow.createdAt)})}
            variant='neutral'
          />
          <Pill
            label={translate('loomLead.LoomLeadDetail.Workflows.Updated', {value: formatDateTime(workflow.updatedAt)})}
            variant='neutral'
          />
          {
            workflow.completedAt &&
            <Pill
              label={translate('loomLead.LoomLeadDetail.Workflows.Completed', {value: formatDateTime(workflow.completedAt)})}
              variant='neutral'
            />
          }
        </Stack>
        {
          description.attention.map((reason) => (
            <Alert key={reason} severity='error'>
              <Typography sx={{fontSize: '13px', fontWeight: 500}}>
                {translate('loomLead.LoomLeadDetail.Workflows.Attention.Title')}
              </Typography>
              <Typography sx={{fontSize: '13px'}}>
                {translate(`loomLead.LoomLeadDetail.Workflows.Attention.${reason}`)}
              </Typography>
            </Alert>
          ))
        }
        {
          isInTeamReview &&
          <Alert severity='warning'>
            {translate('loomLead.LoomLeadDetail.Workflows.Attention.TeamReview')}
          </Alert>
        }
        <LargeTopStepper
          steps={LEAD_INTAKE_STEPS.length}
          activeStep={description.activeStep}
          stepLabels={stepLabels}
        />
        <Box
          sx={{
            display: 'grid',
            border: '1px solid #E8E4DE',
            borderRadius: '12px',
            overflow: 'hidden',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {
            details.map((detail) => (
              <DetailCell
                key={detail.key}
                label={detail.label}
                value={detail.value}
              />
            ))
          }
        </Box>
        {
          workflow.timeline.length > 0 &&
          <Box>
            <Button
              variant='text'
              onClick={() => setShowTimeline((isShown) => !isShown)}
              sx={{textTransform: 'none', px: 0}}
            >
              {
                showTimeline ?
                  translate('loomLead.LoomLeadDetail.Workflows.Timeline.Hide') :
                  translate('loomLead.LoomLeadDetail.Workflows.Timeline.Show', {count: workflow.timeline.length})
              }
            </Button>
            {
              showTimeline &&
              <WorkflowTimeline timeline={workflow.timeline} />
            }
          </Box>
        }
      </Stack>
      {
        showCompleteDialog &&
        <WorkflowCompleteDialog
          clientId={clientId}
          workflowId={workflow.id}
          onClose={() => setShowCompleteDialog(false)}
          onCompleted={handleCompleted}
        />
      }
    </Paper>
  );
};
WorkflowCard.propTypes = {
  clientId: PropTypes.string.isRequired,
  workflow: PropTypes.object.isRequired,
  completionRequested: PropTypes.bool.isRequired,
  onCompleted: PropTypes.func.isRequired,
};

export default React.memo(WorkflowCard);
